/**
 * Typed HTTP wrappers for the form-caches backend API.
 *
 * Mirrors Architecture §9.5. `createFormCacheApi` receives the consumer's
 * httpClient so auth headers and base URL are fully controlled by the caller.
 */

import type { HttpClient, FormCacheDTO } from './FormCacheSelector.types';

/**
 * TanStack Query key factory for form-cache resources.
 *
 * Always invoke `formCacheKeys.all(cacheId)` instead of hardcoding the key
 * tuple in components — it keeps invalidation calls in sync.
 */
export const formCacheKeys = {
  all: (cacheId: string) => ['form-caches', cacheId] as const,
};

export function createFormCacheApi(client: HttpClient) {
  return {
    /**
     * List all form-cache entries for the given `cacheId` and the current user.
     *
     * `cacheId` is URL-encoded so it can safely contain `/`, `?`, `#`, etc.
     */
    getAllByUser: (cacheId: string): Promise<FormCacheDTO[]> =>
      client.get(`/form-caches/${encodeURIComponent(cacheId)}`),

    /**
     * Fetch a single entry by id. Returns `unknown` because the persisted JSON
     * shape depends on `TFilter` and is validated by `safeParse` at the call site.
     */
    getById: (id: string): Promise<unknown> =>
      client.get(`/form-caches/entries/${id}`),

    upsert: (payload: {
      cacheId: string;
      name: string;
      filter: unknown;
    }): Promise<{ id: string }> => client.post('/form-caches', payload),

    updateLastInUse: (id: string): Promise<boolean> =>
      client.patch(`/form-caches/entries/${id}/last-in-use`),

    delete: (id: string): Promise<boolean> =>
      client.delete(`/form-caches/entries/${id}`),

    rename: (id: string, newName: string): Promise<boolean> =>
      client.patch(`/form-caches/entries/${id}/rename`, { newName }),
  };
}

export type FormCacheApi = ReturnType<typeof createFormCacheApi>;
